import { RequestCustomFieldModel, useGetUserSession } from '@apono/client-app-sdk';
import { useMemo, useState } from 'react';
import LabeledTextField from '../ui/LabeledTextField';
import { Combobox, ComboboxOption } from '../ui/Combobox/Combobox';
import { Stack } from '@mui/material';

export type CustomField = { [key: string]: string };

const CustomFieldTypes = {
  Text: 'TEXT',
  Select: 'SELECT',
};

export function RequestCustomFields({ onChange }: { onChange: (value: CustomField) => void }) {
  const { data: userSession } = useGetUserSession();

  const userSettingRequestCustomFields = useMemo(() => userSession?.settings.request_custom_fields, [userSession]);

  return (
    <RequestCustomFieldsInner userSettingRequestCustomFields={userSettingRequestCustomFields} onChange={onChange} />
  );
}

function RequestCustomFieldsInner({
  userSettingRequestCustomFields,
  onChange,
}: {
  userSettingRequestCustomFields?: RequestCustomFieldModel[];
  onChange: (value: CustomField) => void;
}) {
  const [customFields, setCustomFields] = useState<CustomField>({});

  const handleChange = (key: string, value: string | null) => {
    let newFields = { ...customFields };
    if (value !== null) {
      newFields = { ...newFields, [key]: value };
    } else {
      delete newFields[key];
    }
    setCustomFields(newFields);
    onChange(newFields);
  };

  return (
    <Stack direction="column" spacing={2}>
      {userSettingRequestCustomFields?.map((customField) => {
        switch (customField.type) {
          case CustomFieldTypes.Text:
            return (
              <LabeledTextField
                multiline
                size="small"
                maxRows={1}
                label={customField.label}
                value={customFields[customField.id] || ''}
                onChange={(e) => handleChange(customField.id, e.target.value)}
                placeholder={customField.placeholder}
                key={customField.id}
                id={customField.id}
                optional={!customField.required}
              />
            );
          case CustomFieldTypes.Select: {
            const options = customField.values.map((value) => ({ value: value.key, label: value.value }));
            const value = options.find((option) => option.value === customFields[customField.id]) || null;
            return (
              <Combobox
                value={options.find((option) => option.value === value?.value) || null}
                label={`${customField.label}${customField.required ? '' : ' (optional)'}`}
                placeholder={customField.placeholder}
                options={options}
                onChange={(e) => handleChange(customField.id, getComboboxValue(e))}
                key={customField.id}
              />
            );
          }
          default:
            return <div>Custom field type not supported</div>;
        }
      })}
    </Stack>
  );
}

function getComboboxValue(value: ComboboxOption | ComboboxOption[] | null): string | null {
  if (value === null) {
    return null;
  } else if (Array.isArray(value)) {
    // If you're sure you won't get an array, you can remove this branch
    return value.map((option) => option.value).join(', ');
  } else {
    return value.value;
  }
}
